import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2, Volume1, Volume2, VolumeX, Camera, Trash2, UserCircle } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";
import AliasesDoUsuario from "../AliasesDoUsuario";
import SenhaForte, { avaliarSenha } from "../SenhaForte";
import { getPublicFileUrl } from "../../config";
import { ACCEPT_ONLY_IMAGES, problemaDoArquivo } from "../../constants/uploads";

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muito curto")
		.max(50, "Muito longo")
		.required("Campo obrigatório"),
	// A regra de senha vem de components/SenhaForte (espelho do helper do
	// backend). Aqui só ligamos o resultado dela ao Formik: o backend revalida
	// de qualquer forma e devolve ERR_WEAK_PASSWORD quando a senha é fraca.
	password: Yup.string().test(
		"senha-forte",
		"Senha fraca",
		function validarSenhaDoFormulario(valor) {
			const { name, email } = this.parent || {};
			const avaliacao = avaliarSenha(valor, { nome: name, email });
			return avaliacao.ok || this.createError({ message: avaliacao.erro });
		}
	),
	email: Yup.string().email("E-mail inválido").required("Campo obrigatório"),
});

const ESCOPO_EM_PALAVRAS = {
	own: "vê apenas os atendimentos que estiverem com ela",
	team: "vê os atendimentos da equipe dela",
	queues: "vê os atendimentos das filas em que participa",
	company: "vê todos os atendimentos da empresa",
};

const resumoDoCargo = (cargo) => {
	if (!cargo) {
		return "Sem cargo, a pessoa entra sem permissão nenhuma. Escolha um.";
	}

	const escopo = ESCOPO_EM_PALAVRAS[cargo.ticketScope];
	const aviso =
		cargo.ticketScope === "own"
			? " Atenção: ela não recebe conversa nova da fila sozinha."
			: "";

	return `Com este cargo, a pessoa ${escopo}.${aviso}`;
};

const UserModal = ({ open, onClose, userId }) => {
	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
		signMessages: true,
		muteGroupSounds: false,
		notificationVolume: 50,
		// Nasce desmarcado: quem não mexer no campo segue igual a antes.
		mustChangePassword: false,
	};

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [whatsappId, setWhatsappId] = useState("");
	const { loading, whatsApps } = useWhatsApps();

	// Preview temporário do arquivo escolhido enquanto o upload não termina — evita
	// piscada de imagem errada quando o arquivo não sobe. Só atualiza a foto do
	// usuário e a UI após 200 do backend (via spread profilePicUrl).
	const [picUploading, setPicUploading] = useState(false);
	const [picPreviewLocal, setPicPreviewLocal] = useState(null);

	// Modal de ajuste de foto (zoom + drag) — recorte em círculo 1:1
	const [cropAberto, setCropAberto] = useState(false);
	const [cropArquivo, setCropArquivo] = useState(null); // File original
	const [cropScale, setCropScale] = useState(1);
	const [cropTranslate, setCropTranslate] = useState({ x: 0, y: 0 });
	const [cropConfirmando, setCropConfirmando] = useState(false);
	const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
	const dragRef = useRef({ dragging: false, startX: 0, startY: 0, tx0: 0, ty0: 0 });
	const areaRef = useRef(null);
	const AREA = 260; // px (view quadrada, sobreposta por um círculo visual)

	// Cargo: define as permissões e o que a pessoa enxerga na lista de
	// atendimentos. Sem este campo, a tela de Cargos não servia para nada —
	// dava para criar cargos e ninguém podia ser posto neles.
	const [cargos, setCargos] = useState([]);
	const [roleId, setRoleId] = useState("");

	const [departments, setDepartments] = useState([]);
	const [departmentId, setDepartmentId] = useState("");

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser((prevState) => {
					return { ...prevState, ...data };
				});
				const userQueueIds = data.queues?.map((queue) => queue.id);
				setSelectedQueueIds(userQueueIds);
				setWhatsappId(data.whatsappId ? data.whatsappId : "");
				setRoleId(data.roleId ? String(data.roleId) : "");
				setDepartmentId(data.departmentId ? String(data.departmentId) : "");
			} catch (err) {
				toastError(err);
			}
		};

		fetchUser();
	}, [userId, open]);

	useEffect(() => {
		if (!open) return;

		(async () => {
			try {
				const { data } = await api.get("/roles");
				setCargos(data || []);
			} catch (err) {
				// Sem a lista, o campo some — não é motivo para impedir de
				// salvar o resto do cadastro.
			}
		})();
	}, [open]);

	// Carrega a lista de departamentos para o campo "Departamento".
	// Sem isto, o array fica sempre vazio e o select nunca aparece.
	useEffect(() => {
		if (!open) return;

		(async () => {
			try {
				const { data } = await api.get("/departments");
				setDepartments(data.departments || data || []);
			} catch (err) {
				// Sem departamentos, o campo some — não bloqueia o cadastro.
			}
		})();
	}, [open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
		setPicPreviewLocal(null);
		setPicUploading(false);
		fecharCrop();
	};

	const fecharCrop = () => {
		setCropAberto(false);
		if (cropArquivo?._previewUrl) {
			try { URL.revokeObjectURL(cropArquivo._previewUrl); } catch (_) {}
		}
		setCropArquivo(null);
		setCropScale(1);
		setCropTranslate({ x: 0, y: 0 });
		setImgNatural({ w: 0, h: 0 });
		const inp = document.getElementById("user-profile-pic-input");
		if (inp) inp.value = "";
	};

	// Calcula fit "contain" inicial: a imagem entra inteira dentro da AREA 260x260
	// e calcula também os limites máximos de translação para não deixar o usuário
	// arrastar a foto pra fora do círculo (evita espaços vazios na borda).
	const inicial = useMemo(() => {
		if (!imgNatural.w || !imgNatural.h) return null;
		const s = Math.max(AREA / imgNatural.w, AREA / imgNatural.h);
		const sw = imgNatural.w * s;
		const sh = imgNatural.h * s;
		const minS = s;
		const maxS = s * 3;
		const maxX = Math.max(0, (sw - AREA) / 2);
		const maxY = Math.max(0, (sh - AREA) / 2);
		return { initialScale: s, minS, maxS, maxX, maxY, sw, sh };
	}, [imgNatural]);

	// Ao mudar escala, limita a translação para a foto continuar cobrindo toda a
	// área do círculo. Isto impede bordas brancas/pretas em volta do avatar.
	useEffect(() => {
		if (!inicial) return;
		const sw = inicial.sw * (cropScale / inicial.initialScale);
		const sh = inicial.sh * (cropScale / inicial.initialScale);
		const mX = Math.max(0, (sw - AREA) / 2);
		const mY = Math.max(0, (sh - AREA) / 2);
		setCropTranslate((t) => ({
			x: Math.max(-mX, Math.min(mX, t.x)),
			y: Math.max(-mY, Math.min(mY, t.y)),
		}));
	}, [cropScale, inicial]);

	const handleAbrirCrop = async (arquivo) => {
		const previewUrl = URL.createObjectURL(arquivo);
		const arquivoComUrl = Object.assign(arquivo, { _previewUrl: previewUrl });
		const img = new Image();
		img.src = previewUrl;
		img.onload = () => {
			const w = img.naturalWidth;
			const h = img.naturalHeight;
			if (!w || !h) {
				toast.error("Não foi possível ler a imagem.");
				try { URL.revokeObjectURL(previewUrl); } catch (_) {}
				return;
			}
			setImgNatural({ w, h });
			const s0 = Math.max(AREA / w, AREA / h);
			setCropScale(s0);
			setCropTranslate({ x: 0, y: 0 });
			setCropArquivo(arquivoComUrl);
			setCropAberto(true);
		};
		img.onerror = () => {
			toast.error("Não foi possível ler a imagem.");
			try { URL.revokeObjectURL(previewUrl); } catch (_) {}
		};
	};

	const handleTrocarFoto = async (e) => {
		const escolhidos = Array.from(e.target.files || []);
		if (escolhidos.length === 0) return;
		const arquivo = escolhidos[0];
		const problema = problemaDoArquivo(arquivo);
		if (problema) {
			toast.error(problema);
			e.target.value = "";
			return;
		}
		const alvoId = userId || loggedInUser?.id;
		if (!alvoId) {
			toast.error("Salve o usuário antes de definir a foto.");
			e.target.value = "";
			return;
		}
		// Não sobe direto: primeiro abre o modal de ajuste/zoom/posicionamento e
		// só depois, ao confirmar, recortamos em canvas e enviamos via FormData.
		handleAbrirCrop(arquivo);
	};

	const handleConfirmarCrop = async () => {
		const alvoId = userId || loggedInUser?.id;
		if (!alvoId) return;
		if (!cropArquivo || !inicial || !imgNatural.w || !imgNatural.h) return;
		setCropConfirmando(true);
		setPicUploading(true);
		try {
			// Aguarda a imagem do blob estar 100% carregada antes de desenhar no canvas
			// (evita drawImage de uma imagem ainda não decodificada que vira tela preta)
			const img = await new Promise((resolve, reject) => {
				const im = new Image();
				im.onload = () => resolve(im);
				im.onerror = reject;
				im.src = cropArquivo._previewUrl;
			});

			const SZ = 512;
			const canvas = document.createElement("canvas");
			canvas.width = SZ;
			canvas.height = SZ;
			const ctx = canvas.getContext("2d");
			if (!ctx) {
				toast.error("Não foi possível recortar a imagem.");
				return;
			}
			ctx.imageSmoothingEnabled = true;
			ctx.imageSmoothingQuality = "high";
			ctx.save();
			ctx.beginPath();
			ctx.arc(SZ / 2, SZ / 2, SZ / 2, 0, Math.PI * 2);
			ctx.closePath();
			ctx.clip();

			const ratio = SZ / AREA;
			const k = cropScale / inicial.initialScale;
			const escalaFinal = ratio * k * inicial.initialScale;
			const centroX = SZ / 2 - (imgNatural.w * escalaFinal) / 2 + cropTranslate.x * ratio;
			const centroY = SZ / 2 - (imgNatural.h * escalaFinal) / 2 + cropTranslate.y * ratio;
			ctx.drawImage(img, centroX, centroY, imgNatural.w * escalaFinal, imgNatural.h * escalaFinal);
			ctx.restore();

			const blob = await new Promise((resolve) =>
				canvas.toBlob((b) => resolve(b || null), "image/jpeg", 0.93)
			);
			if (!blob) {
				toast.error("Não foi possível gerar a imagem final.");
				return;
			}
			const previewUrl = URL.createObjectURL(blob);
			setPicPreviewLocal(previewUrl);

			const fileName = `avatar-${Date.now()}.jpg`;
			const formData = new FormData();
			formData.append("file", blob, fileName);
			const { data } = await api.post(`/users/${alvoId}/profile-pic`, formData);
			setUser((u) => ({ ...u, profilePicUrl: data.profilePicUrl }));
			toast.success("Foto atualizada.");
			setTimeout(() => {
				try { URL.revokeObjectURL(previewUrl); } catch (_) {}
				setPicPreviewLocal(null);
			}, 800);
			fecharCrop();
		} catch (err) {
			toastError(err);
		} finally {
			setCropConfirmando(false);
			setPicUploading(false);
			setPicPreviewLocal(null);
		}
	};

	// Drag handlers da área do círculo
	const onPointerDownCrop = (e) => {
		if (!areaRef.current) return;
		e.currentTarget.setPointerCapture?.(e.pointerId);
		dragRef.current = {
			dragging: true,
			startX: e.clientX,
			startY: e.clientY,
			tx0: cropTranslate.x,
			ty0: cropTranslate.y,
		};
	};
	const onPointerMoveCrop = (e) => {
		if (!dragRef.current.dragging || !inicial) return;
		const dx = e.clientX - dragRef.current.startX;
		const dy = e.clientY - dragRef.current.startY;
		const sw = inicial.sw * (cropScale / inicial.initialScale);
		const sh = inicial.sh * (cropScale / inicial.initialScale);
		const mX = Math.max(0, (sw - AREA) / 2);
		const mY = Math.max(0, (sh - AREA) / 2);
		setCropTranslate({
			x: Math.max(-mX, Math.min(mX, dragRef.current.tx0 + dx)),
			y: Math.max(-mY, Math.min(mY, dragRef.current.ty0 + dy)),
		});
	};
	const onPointerUpCrop = () => { dragRef.current.dragging = false; };

	const onWheelCrop = (e) => {
		if (!inicial) return;
		e.preventDefault();
		const delta = -e.deltaY * 0.0015;
		setCropScale((s) => Math.max(inicial.minS, Math.min(inicial.maxS, s * (1 + delta))));
	};

	const handleRemoverFoto = async () => {
		const alvoId = userId || loggedInUser?.id;
		if (!alvoId) return;
		try {
			await api.delete(`/users/${alvoId}/profile-pic`);
			setUser((u) => ({ ...u, profilePicUrl: null }));
			toast.success("Foto removida.");
		} catch (err) {
			toastError(err);
		}
	};

	const handleSaveUser = async (values) => {
		const userData = {
			...values,
			whatsappId,
			queueIds: selectedQueueIds,
			departmentId: departmentId ? Number(departmentId) : null,
		};
		try {
			// O cargo tem rota própria (com registro de quem mudou), então vai
			// numa chamada separada — depois de existir o usuário.
			let alvo = userId;

			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				const { data } = await api.post("/users", userData);
				alvo = data?.id;
			}

			if (alvo) {
				await api.put(`/users/${alvo}/role`, {
					roleId: roleId ? Number(roleId) : null,
				});
			}

			toast.success(i18n.t("userModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
				<DialogHeader>
					<DialogTitle>
						{userId
							? i18n.t("userModal.title.edit")
							: i18n.t("userModal.title.add")}
					</DialogTitle>
				</DialogHeader>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, setFieldValue, touched, errors, isSubmitting }) => (
						<Form className="space-y-4">
							{/* Duas colunas para o modal abrir inteiro numa tela normal: à
							    esquerda quem é a pessoa e como ela entra (foto, dados,
							    senha, cargo); à direita onde ela atende e como é avisada.
							    Em tela estreita as colunas empilham sozinhas. */}
							<div className="grid gap-4 md:grid-cols-[1.1fr_1fr] md:gap-5">
							{/* ─── Coluna 1: cadastro e acesso ─── */}
							<div className="space-y-3">
							{/* Foto de perfil do usuário — a mesma tela de Perfil (clicou na
							    próprio nome ali do menu) é a primeira coisa que aparece. */}
							<div className="flex flex-col items-center gap-2 sm:flex-row sm:items-center sm:justify-center sm:gap-4 rounded-lg border border-border bg-card p-3 shadow-sm">
								<div className="relative shrink-0">
									<div className="h-20 w-20 overflow-hidden rounded-full border-2 border-border bg-muted flex items-center justify-center">
										{picPreviewLocal ? (
											<img src={picPreviewLocal} alt="preview" className="h-full w-full object-cover" />
										) : values.profilePicUrl ? (
											<img
												src={getPublicFileUrl(values.profilePicUrl)}
												alt={values.name || "avatar"}
												className="h-full w-full object-cover"
											/>
										) : (
											<UserCircle className="h-14 w-14 text-muted-foreground shrink-0" />
										)}
									</div>
									{picUploading && (
										<div className="absolute inset-0 flex items-center justify-center rounded-full bg-background/70">
											<Loader2 className="h-6 w-6 animate-spin text-primary" />
										</div>
									)}
								</div>
								<div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-center sm:gap-2">
									<input
										id="user-profile-pic-input"
										type="file"
										hidden
										accept={ACCEPT_ONLY_IMAGES}
										onChange={handleTrocarFoto}
										disabled={picUploading}
									/>
									<Button
										type="button"
										variant="outline"
										size="sm"
										disabled={picUploading || !userId}
										onClick={() =>
											document.getElementById("user-profile-pic-input")?.click()
										}
									>
										<Camera className="h-4 w-4" />
										{picUploading ? "Enviando…" : "Trocar foto"}
									</Button>
									{values.profilePicUrl && (
										<Button
											type="button"
											variant="ghost"
											size="sm"
											className="text-destructive hover:text-destructive"
											disabled={picUploading || !userId}
											onClick={handleRemoverFoto}
										>
											<Trash2 className="h-4 w-4" />
											Remover
										</Button>
									)}
								</div>
							</div>
							{/* Identificação: Nome e E-mail são campos de texto da
							    mesma altura, então dividem a linha sem desalinhar. */}
							<div className="flex gap-3">
								<div className="flex-1 space-y-1.5">
									<Label htmlFor="name">{i18n.t("userModal.form.name")}</Label>
									<Field as={Input} id="name" name="name" autoFocus />
									{/* A linha de erro fica sempre reservada (min-h): aparecer
									    e sumir conforme a validação não pode empurrar o
									    formulário para baixo. */}
									<p className="min-h-[1rem] text-xs leading-4 text-destructive">
										{touched.name && errors.name ? errors.name : ""}
									</p>
								</div>
								<div className="flex-1 space-y-1.5">
									<Label htmlFor="email">{i18n.t("userModal.form.email")}</Label>
									<Field as={Input} id="email" name="email" />
									<p className="min-h-[1rem] text-xs leading-4 text-destructive">
										{touched.email && errors.email ? errors.email : ""}
									</p>
								</div>
							</div>

							{/* Acesso num cartão só: a senha ocupa a linha inteira (medidor,
							    requisitos e gerador precisam de espaço) e a troca obrigatória
							    fica logo abaixo, no mesmo bloco, em vez de ser uma caixa
							    solta no meio do formulário. */}
							<div className="space-y-3 rounded-lg border border-border bg-card p-3">
								<Field name="password">
									{({ field, form }) => (
										<SenhaForte
											id="password"
											label={i18n.t("userModal.form.password")}
											value={field.value || ""}
											onChange={(valor) =>
												form.setFieldValue("password", valor)
											}
											onBlur={() => form.setFieldTouched("password", true)}
											nome={values.name}
											email={values.email}
											touched={form.touched.password}
											error={form.errors.password}
											// Senha gerada é aleatória: já marca a troca
											// obrigatória no próximo login.
											onGerar={() =>
												form.setFieldValue("mustChangePassword", true)
											}
										/>
									)}
								</Field>

								{/* Troca obrigatória: quando ligada, a pessoa até entra, mas
								    cai numa tela bloqueante e só usa o sistema depois de
								    definir a senha dela. Útil quando o admin escreve (ou
								    gera) a senha inicial. */}
								<div className="flex items-start gap-2.5 border-t border-border pt-3">
									<Checkbox
										id="mustChangePassword"
										className="mt-0.5"
										checked={Boolean(values.mustChangePassword)}
										onCheckedChange={(marcado) =>
											setFieldValue("mustChangePassword", Boolean(marcado))
										}
									/>
									<div className="space-y-0.5">
										<Label
											htmlFor="mustChangePassword"
											className="cursor-pointer"
										>
											Exigir troca de senha no próximo login
										</Label>
										<p className="text-[11px] text-muted-foreground">
											{userId
												? "A pessoa entra com esta senha e só usa o sistema depois de trocar por uma nova."
												: "A senha combinada aqui é temporária: no primeiro acesso a pessoa é obrigada a trocar."}
										</p>
									</div>
								</div>
							</div>

							<Can
								role={loggedInUser.profile}
								perform="user-modal:editProfile"
								yes={() => (
									<div className="space-y-1.5">
										<Label>{i18n.t("userModal.form.profile")}</Label>
										<Select
											value={values.profile}
											onValueChange={(v) => setFieldValue("profile", v)}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="admin">Admin</SelectItem>
												<SelectItem value="user">User</SelectItem>
											</SelectContent>
										</Select>
									</div>
								)}
							/>

							<Can
								role={loggedInUser.profile}
								perform="user-modal:editProfile"
								yes={() =>
									cargos.length > 0 && (
										<div className="space-y-1.5">
											<Label>Cargo</Label>
											<Select
												value={roleId || "none"}
												onValueChange={(v) => setRoleId(v === "none" ? "" : v)}
											>
												<SelectTrigger>
													<SelectValue placeholder="Sem cargo" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="none">Sem cargo</SelectItem>
													{cargos.map((cargo) => (
														<SelectItem key={cargo.id} value={String(cargo.id)}>
															{cargo.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											{/* O que o cargo escolhido significa, em uma frase: quem
											    cadastra usuário não deveria precisar abrir outra tela
											    para descobrir. */}
											<p className="text-xs leading-relaxed text-muted-foreground">
												{resumoDoCargo(
													cargos.find((c) => String(c.id) === String(roleId))
												)}
											</p>
										</div>
									)
								}
							/>

							</div>

							{/* ─── Coluna 2: onde atende e como é avisada ─── */}
							<div className="space-y-3">
							<Can
								role={loggedInUser.profile}
								perform="user-modal:editProfile"
								yes={() =>
									departments.length > 0 && (
										<div className="space-y-1.5">
											<Label>Departamento</Label>
											<Select
												value={departmentId || "nenhum"}
												onValueChange={(v) =>
													setDepartmentId(v === "nenhum" ? "" : v)
												}
											>
												<SelectTrigger>
													<SelectValue placeholder="Sem departamento" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="nenhum">Sem departamento</SelectItem>
													{departments.map((d) => (
														<SelectItem key={d.id} value={String(d.id)}>
															{d.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									)
								}
							/>

							<Can
								role={loggedInUser.profile}
								perform="user-modal:editQueues"
								yes={() => (
									<QueueSelect
										selectedQueueIds={selectedQueueIds}
										onChange={(values) => setSelectedQueueIds(values)}
									/>
								)}
							/>
							<Can
								role={loggedInUser.profile}
								perform="user-modal:editQueues"
								yes={() =>
									!loading && (
										<div className="space-y-1.5">
											<Label>{i18n.t("userModal.form.whatsapp")}</Label>
											<Select
												value={whatsappId ? String(whatsappId) : "none"}
												onValueChange={(v) => setWhatsappId(v === "none" ? "" : v)}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="none">&nbsp;</SelectItem>
													{whatsApps.map((whatsapp) => (
														<SelectItem key={whatsapp.id} value={String(whatsapp.id)}>
															{whatsapp.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									)
								}
							/>

							{/* Nomes alternativos: quem edita o cadastro é quem define
							    com que nome a pessoa se apresenta em cada fila. */}
							<AliasesDoUsuario
								userId={userId}
								queueIds={selectedQueueIds}
							/>

							{/* Preferências de notificação individuais. */}
							<div className="space-y-3">
								<div className="rounded-lg border border-border bg-card p-3 shadow-sm">
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-2.5">
											{values.muteGroupSounds ? (
												<VolumeX className="h-4 w-4 text-muted-foreground" />
											) : (
												<Volume2 className="h-4 w-4 text-muted-foreground" />
											)}
											<div className="space-y-0.5">
												<p className="text-sm font-medium leading-none">
													Silenciar som de grupos
												</p>
												<p className="text-xs leading-none text-muted-foreground">
													Conversas individuais continuam tocando normalmente.
												</p>
											</div>
										</div>
										<Switch
											id="muteGroupSounds"
											checked={Boolean(values.muteGroupSounds)}
											onCheckedChange={(v) =>
												setFieldValue("muteGroupSounds", v)
											}
										/>
									</div>
								</div>

								<div className="rounded-lg border border-border bg-card p-3 shadow-sm">
									<div className="space-y-2">
										<div className="flex items-center justify-between gap-2">
											<div className="flex items-center gap-2.5">
												{(() => {
													const v = Math.max(
														0,
														Math.min(
															100,
															Number(values.notificationVolume) || 0
														)
													);
													if (v <= 0)
														return (
															<VolumeX className="h-4 w-4 text-muted-foreground" />
														);
													if (v <= 40)
														return (
															<Volume1 className="h-4 w-4 text-muted-foreground" />
														);
													return (
														<Volume2 className="h-4 w-4 text-muted-foreground" />
													);
												})()}
												<div className="space-y-0.5">
													<p className="text-sm font-medium leading-none">
														Volume notificações
													</p>
													<p className="text-xs leading-none text-muted-foreground">
														Chamada, chat interno e sons de mensagem nova.
													</p>
												</div>
											</div>
											<span className="inline-flex h-7 min-w-[3rem] items-center justify-center rounded-md border border-border bg-muted px-2 text-[11px] font-medium text-muted-foreground tabular-nums">
												{Math.max(
													0,
													Math.min(100, Number(values.notificationVolume) || 0)
												)}
												%
											</span>
										</div>
										<div className="px-1">
											<input
												type="range"
												min={0}
												max={100}
												step={1}
												value={Math.max(
													0,
													Math.min(100, Number(values.notificationVolume) || 0)
												)}
												onChange={(e) => {
													const v = Math.max(
														0,
														Math.min(100, Number(e.target.value) || 0)
													);
													setFieldValue("notificationVolume", v);
												}}
												className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-muted [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-sm [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary"
											/>
											<div className="mt-1 flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
												<span>Mudo</span>
												<span>Padrão 50%</span>
												<span>Máximo</span>
											</div>
										</div>
									</div>
								</div>
							</div>
							</div>
							</div>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									{userId
										? i18n.t("userModal.buttons.okEdit")
										: i18n.t("userModal.buttons.okAdd")}
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>

			{/* Segundo Dialog interno: ajuste da foto de perfil — aparece sempre no topo, acima do modal. */}
			<Dialog open={cropAberto} onOpenChange={(o) => !o && fecharCrop()}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Ajustar foto</DialogTitle>
						<p className="text-xs text-muted-foreground pt-1">
							Arraste para mover e use o controle de zoom para enquadrar.
						</p>
					</DialogHeader>
					<div className="flex flex-col items-center gap-4 py-2">
						{/* Área interativa de preview circular 260x260 */}
						<div
							ref={areaRef}
							className={`relative select-none rounded-full border-2 border-primary/40 shadow-inner bg-muted overflow-hidden touch-none ${
								dragRef.current?.dragging ? "cursor-grabbing" : "cursor-grab"
							}`}
							style={{ width: AREA, height: AREA }}
							onPointerDown={onPointerDownCrop}
							onPointerMove={onPointerMoveCrop}
							onPointerUp={onPointerUpCrop}
							onPointerCancel={onPointerUpCrop}
							onWheel={onWheelCrop}
						>
							{cropArquivo?._previewUrl ? (
								(() => {
									const k = inicial ? cropScale / inicial.initialScale : 1;
									const w = inicial ? inicial.sw * k : AREA;
									const h = inicial ? inicial.sh * k : AREA;
									const left = (AREA - w) / 2 + cropTranslate.x;
									const top = (AREA - h) / 2 + cropTranslate.y;
									return (
										<img
											draggable={false}
											src={cropArquivo._previewUrl}
											alt="ajuste"
											className="absolute max-w-none pointer-events-none will-change-transform object-cover"
											style={{ width: w, height: h, left, top }}
										/>
									);
								})()
							) : null}
						</div>

						<div className="w-full max-w-[260px] space-y-2">
							<div className="flex items-center justify-between text-xs text-muted-foreground">
								<span>Zoom</span>
								<span className="font-mono tabular-nums">
									{inicial ? `${(cropScale / inicial.initialScale).toFixed(2)}×` : "1.00×"}
								</span>
							</div>
							<input
								type="range"
								min={inicial ? inicial.minS : 1}
								max={inicial ? inicial.maxS : 3}
								step={inicial ? Math.max(0.0001, inicial.minS / 500) : 0.01}
								value={cropScale}
								onChange={(e) => setCropScale(parseFloat(e.target.value))}
								className="w-full accent-primary"
								disabled={!inicial}
							/>
						</div>
					</div>
					<DialogFooter className="pt-2">
						<Button
							type="button"
							variant="outline"
							onClick={fecharCrop}
							disabled={cropConfirmando}
						>
							Cancelar
						</Button>
						<Button
							type="button"
							onClick={handleConfirmarCrop}
							disabled={cropConfirmando || !inicial}
						>
							{cropConfirmando ? (
								<>
									<Loader2 className="h-4 w-4 animate-spin" />
									Processando…
								</>
							) : (
								"Confirmar"
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</Dialog>
	);
};

export default UserModal;
